import "../css/AddStockist.css";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { BASE_URL } from "../config";
import { getAuthHeaders } from "../services/useAuth";
import Toaster, { showSuccessToast, showErrorToast } from "../dist/Toaster";
import { Import } from "lucide-react";
import CustomButton from "./CustomButton";

const AddStockist = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    stockistName: "",
    contactPerson: "",
    country: "India",
    state: "",
    city: "",
    headquarter: "",
    division: "",
    dob: "",
    marriageAnniversary: "",
    address: "",
    mobile: "",
    phone: "",
    email: "",
    pin: "",
    gstin: "",
    panNumber: "",
    dlNumber: "",
    cstNumber: "",
    mappingCode: "",
  });

  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [headquarters, setHeadquarters] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    // Fetch states
    fetch(`${BASE_URL}/api/states`, {
      method: "GET",
      headers: getAuthHeaders(),
    })
      .then((res) => res.json())
      .then((data) => {
        setStates(data);
      })
      .catch((err) => console.error("Error fetching states:", err));

    // Fetch divisions
    fetch(`${BASE_URL}/api/divisions`, {
      method: "GET",
      headers: getAuthHeaders(),
    })
      .then((res) => res.json())
      .then((data) => {
        setDivisions(data);
      })
      .catch((err) => console.error("Error fetching divisions:", err));
  }, []);

  // Enhanced validation functions
  const validateEmail = (email) => {
    if (!email) return true; // Not required field, so empty is valid
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const validateMobile = (mobile) => {
    if (!mobile) return false; // Mobile is required
    return /^\d{10}$/.test(mobile);
  };

  const validateDate = (date, fieldName) => {
    if (!date) return true; // Not required fields

    const selectedDate = new Date(date);
    const today = new Date();

    // For DOB, ensure it's not in future and person is at least 18 years old
    if (fieldName === "dob") {
      const minDate = new Date();
      minDate.setFullYear(today.getFullYear() - 18);
      return selectedDate <= today && selectedDate <= minDate;
    }

    // For marriage anniversary, just ensure it's not in future
    return selectedDate <= today;
  };

  const getDateErrorMessage = (date, fieldName) => {
    if (!date) return "";

    const selectedDate = new Date(date);
    const today = new Date();

    if (fieldName === "dob") {
      const minDate = new Date();
      minDate.setFullYear(today.getFullYear() - 18);

      if (selectedDate > today) {
        return "Date cannot be in the future";
      }
      if (selectedDate > minDate) {
        return "Person must be at least 18 years old";
      }
    } else if (selectedDate > today) {
      return "Date cannot be in the future";
    }

    return "";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    // For numeric fields, only allow numbers or empty string
    if (["mobile", "phone", "pin", "mappingCode"].includes(name)) {
      if (value === "" || /^\d+$/.test(value)) {
        setFormData({ ...formData, [name]: value });
      }
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleStateChange = (e) => {
    const selectedState = e.target.value;
    setFormData({
      ...formData,
      state: selectedState,
      city: "",
      headquarter: "",
    });
    setCities([]);
    setHeadquarters([]);

    // Clear errors for dependent fields
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors.city;
      delete newErrors.headquarter;
      return newErrors;
    });

    if (selectedState) {
      fetch(`${BASE_URL}/api/cities/${selectedState}`, {
        headers: getAuthHeaders(),
      })
        .then((res) => res.json())
        .then((data) => {
          setCities(data);
        })
        .catch((err) => console.error("Error fetching cities:", err));
    }
  };

  const handleCityChange = async (e) => {
    const selectedCity = e.target.value;
    setFormData({ ...formData, city: selectedCity, headquarter: "" });

    // Clear errors for dependent fields
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors.headquarter;
      return newErrors;
    });

    if (selectedCity) {
      try {
        const response = await fetch(
          `${BASE_URL}/api/headquarters/${selectedCity}`,
          {
            headers: getAuthHeaders(),
          }
        );
        const data = await response.json();
        setHeadquarters(data);

        if (data.length > 0) {
          setFormData((prev) => ({
            ...prev,
            headquarter: data[0].id.toString(),
          }));
        }
      } catch (err) {
        console.error("Error fetching headquarters:", err);
      }
    } else {
      setHeadquarters([]);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const requiredFields = [
      "stockistName",
      "contactPerson",
      "state",
      "city",
      "headquarter",
      "division",
      "mobile",
    ];

    // Check required fields
    requiredFields.forEach((field) => {
      if (!formData[field]) {
        newErrors[field] = "This field is required";
      }
    });

    // Validate mobile number
    if (!validateMobile(formData.mobile)) {
      newErrors.mobile = formData.mobile
        ? "Mobile number must be 10 digits"
        : "Mobile number is required";
    }

    // Validate email
    if (formData.email && !validateEmail(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    // Validate dates
    const dobError = getDateErrorMessage(formData.dob, "dob");
    if (dobError) {
      newErrors.dob = dobError;
    }

    const anniversaryError = getDateErrorMessage(
      formData.marriageAnniversary,
      "marriageAnniversary"
    );
    if (anniversaryError) {
      newErrors.marriageAnniversary = anniversaryError;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      showErrorToast("Please fix the errors in the form");
      return;
    }

    const formattedData = {
      stockist_name: formData.stockistName,
      contact_person: formData.contactPerson,
      country: formData.country,
      state_id: parseInt(formData.state, 10),
      city_id: parseInt(formData.city, 10),
      headquarter_id: parseInt(formData.headquarter, 10),
      division_id: parseInt(formData.division, 10),
      mobile: formData.mobile,
      gstin: formData.gstin,
      updated_by: localStorage.getItem("loggedInUser") || "Admin",
      start_time: new Date().toISOString(),
      address: formData.address || null,
      phone: formData.phone || null,
      email: formData.email || null,
      pin: formData.pin || null,
      pan_number: formData.panNumber || null,
      dl_number: formData.dlNumber || null,
      cst_number: formData.cstNumber || null,
      mapping_code: formData.mappingCode || null,
      dob: formData.dob || null,
      marriage_anniversary: formData.marriageAnniversary || null,
    };

    try {
      const response = await fetch(`${BASE_URL}/api/stockist`, {
        method: "POST",
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formattedData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to add stockist");
      }

      showSuccessToast("Stockist added successfully!");

  setFormData({
    stockistName: "",
    contactPerson: "",
    country: "India",
    state: "",
    city: "",
    headquarter: "",
    division: "",
    dob: "",
    marriageAnniversary: "",
    address: "",
    mobile: "",
    phone: "",
    email: "",
    pin: "",
    gstin: "",
    panNumber: "",
    dlNumber: "",
    cstNumber: "",
    mappingCode: "",
  });

  setCities([]);
  setHeadquarters([]);
  setErrors({});
      } catch (error) {
        console.error("Error:", error);
        showErrorToast(error.message || "Failed to connect to the server.");
    }
  };

  return (
    <div className="p-2  border-2 border-gray-300 rounded-lg bg-white">
      <div className="">
        <Toaster />
        <div className="mt-3">
          <h1 class="text-xl sm:text-2xl font-bold text-center text-gray-800 ">
            <span class="text-blue-700">Add </span>
            <span class="text-blue-500">Stockist</span>
          </h1>
        </div>
        <form onSubmit={handleSubmit}>
          {/* Basic Details */}
          <fieldset className="section">
            <legend className="bg-blue-500 border rounded text-white px-3">
              Basic Details
            </legend>
            <div className="grid-container">
              <label className="required-label">STOCKIST NAME</label>
              <div className="input-field-container">
                <input
                  type="text"
                  name="stockistName"
                  value={formData.stockistName}
                  onChange={handleChange}
                  className={errors.stockistName ? "input-error" : ""}
                />
                {errors.stockistName && (
                  <span className="validation-error">
                    {errors.stockistName}
                  </span>
                )}
              </div>

              <label className="required-label">CONTACT PERSON</label>
              <div className="input-field-container">
                <input
                  type="text"
                  name="contactPerson"
                  value={formData.contactPerson}
                  onChange={handleChange}
                  className={errors.contactPerson ? "input-error" : ""}
                />
                {errors.contactPerson && (
                  <span className="validation-error">
                    {errors.contactPerson}
                  </span>
                )}
              </div>

              <label className="required-label">COUNTRY</label>
              <select
                name="country"
                value={formData.country}
                onChange={handleChange}
              >
                <option value="India">INDIA</option>
              </select>

              <label className="required-label">STATE</label>
              <div className="input-field-container">
                <select
                  name="state"
                  value={formData.state}
                  onChange={handleStateChange}
                  className={errors.state ? "input-error" : ""}
                >
                  <option value="">Select</option>
                  {states.map((state) => (
                    <option key={state.id} value={state.id}>
                      {state.name}
                    </option>
                  ))}
                </select>
                {errors.state && (
                  <span className="validation-error">{errors.state}</span>
                )}
              </div>

              <label className="required-label">CITY</label>
              <div className="input-field-container">
                <select
                  name="city"
                  value={formData.city}
                  onChange={handleCityChange}
                  className={errors.city ? "input-error" : ""}
                >
                  <option value="">Select</option>
                  {cities.map((city) => (
                    <option key={city.id} value={city.id}>
                      {city.name}
                    </option>
                  ))}
                </select>
                {errors.city && (
                  <span className="validation-error">{errors.city}</span>
                )}
              </div>

              <label className="required-label">HQ</label>
              <div className="input-field-container">
                <select
                  name="headquarter"
                  value={formData.headquarter}
                  onChange={handleChange}
                  className={errors.headquarter ? "input-error" : ""}
                >
                  {headquarters.length > 0 ? (
                    headquarters.map((hq) => (
                      <option key={hq.id} value={hq.id}>
                        {hq.name}
                      </option>
                    ))
                  ) : (
                    <option value="">No headquarters available</option>
                  )}
                </select>
                {errors.headquarter && (
                  <span className="validation-error">{errors.headquarter}</span>
                )}
              </div>

              <label className="required-label">DIVISION</label>
              <div className="input-field-container">
                <select
                  name="division"
                  value={formData.division}
                  onChange={handleChange}
                  className={errors.division ? "input-error" : ""}
                >
                  <option value="">Select</option>
                  {divisions.map((division) => (
                    <option key={division.id} value={division.id}>
                      {division.name}
                    </option>
                  ))}
                </select>
                {errors.division && (
                  <span className="validation-error">{errors.division}</span>
                )}
              </div>

              <label className="required-label">MOBILE</label>
              <div className="input-field-container">
                <input
                  type="text"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleChange}
                  maxLength="10"
                  placeholder="Enter mobile number"
                  className={errors.mobile ? "input-error" : ""}
                />
                {errors.mobile && (
                  <span className="validation-error">{errors.mobile}</span>
                )}
              </div>
            </div>
          </fieldset>

          {/* Contact Details */}
          <fieldset className="section">
            <legend className="bg-blue-500 border rounded text-white px-3">
              Contact Details
            </legend>
            <div className="grid-container">
              <label>ADDRESS</label>
              <textarea
                name="address"
                value={formData.address}
                onChange={handleChange}
                className={errors.address ? "input-error" : ""}
              />

              <label>DATE OF BIRTH</label>
              <div className="input-field-container">
                <input
                  type="date"
                  name="dob"
                  value={formData.dob}
                  onChange={handleChange}
                  max={new Date().toISOString().split("T")[0]}
                  className={errors.dob ? "input-error" : ""}
                />
                {errors.dob && (
                  <span className="validation-error">{errors.dob}</span>
                )}
              </div>

              <label>PHONE</label>
              <div className="input-field-container">
                <input
                  type="text"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  maxLength="10"
                  minLength="10"
                  className={errors.phone ? "input-error" : ""}
                />
                {errors.phone && (
                  <span className="validation-error">{errors.phone}</span>
                )}
              </div>

              <label>MARRIAGE ANNIVERSARY</label>
              <div className="input-field-container">
                <input
                  type="date"
                  name="marriageAnniversary"
                  value={formData.marriageAnniversary}
                  onChange={handleChange}
                  max={new Date().toISOString().split("T")[0]}
                  className={errors.marriageAnniversary ? "input-error" : ""}
                />
                {errors.marriageAnniversary && (
                  <span className="validation-error">
                    {errors.marriageAnniversary}
                  </span>
                )}
              </div>

              <label>EMAIL</label>
              <div className="input-field-container">
                <input
                  type="text"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter email address"
                  className={errors.email ? "input-error" : ""}
                />
                {errors.email && (
                  <span className="validation-error">{errors.email}</span>
                )}
              </div>

              <label>PIN</label>
              <div className="input-field-container">
                <input
                  type="text"
                  name="pin"
                  value={formData.pin}
                  onChange={handleChange}
                  maxLength="6"
                  className={errors.pin ? "input-error" : ""}
                />
                {errors.pin && (
                  <span className="validation-error">{errors.pin}</span>
                )}
              </div>
            </div>
          </fieldset>

          {/* Financial Details */}
          <fieldset className="section">
            <legend className="bg-blue-500 border rounded text-white px-3">
              Financial Details
            </legend>
            <div className="grid-container">
              <label>GSTIN</label>
              <div className="input-field-container">
                <input
                  type="text"
                  name="gstin"
                  value={formData.gstin}
                  onChange={handleChange}
                  maxLength="15"
                  minLength="15"
                  className={errors.gstin ? "input-error" : ""}
                />
                {errors.gstin && (
                  <span className="validation-error">{errors.gstin}</span>
                )}
              </div>

              <label>PAN NUMBER</label>
              <div className="input-field-container">
                <input
                  type="text"
                  name="panNumber"
                  value={formData.panNumber}
                  onChange={handleChange}
                  maxLength="10"
                  minLength="10"
                  className={errors.panNumber ? "input-error" : ""}
                />
                {errors.panNumber && (
                  <span className="validation-error">{errors.panNumber}</span>
                )}
              </div>

              <label>DL NUMBER</label>
              <div className="input-field-container">
                <input
                  type="text"
                  name="dlNumber"
                  value={formData.dlNumber}
                  onChange={handleChange}
                  className={errors.dlNumber ? "input-error" : ""}
                />
                {errors.dlNumber && (
                  <span className="validation-error">{errors.dlNumber}</span>
                )}
              </div>

              <label>CST NUMBER</label>
              <div className="input-field-container">
                <input
                  type="text"
                  name="cstNumber"
                  value={formData.cstNumber}
                  onChange={handleChange}
                  className={errors.cstNumber ? "input-error" : ""}
                />
                {errors.cstNumber && (
                  <span className="validation-error">{errors.cstNumber}</span>
                )}
              </div>

              <label>MAPPING CODE</label>
              <div className="input-field-container">
                <input
                  type="text"
                  name="mappingCode"
                  value={formData.mappingCode}
                  onChange={handleChange}
                  className={errors.mappingCode ? "input-error" : ""}
                />
                {errors.mappingCode && (
                  <span className="validation-error">{errors.mappingCode}</span>
                )}
              </div>
            </div>
          </fieldset>

          {/* Buttons */}
          <div className="butons flex justify-center">
            <CustomButton label="Submit" variant="go" type="submit" />
            <CustomButton
              label="Return"
              variant="cancel"
              type="button"
              onClick={() => navigate("/liststockist")}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddStockist;
